export default null;

/**
 * @param {number} enteredPrice - The price the admin typed (GA for including, ST for excluding)
 * @param {number} vatPercent   - Rate, e.g. 8 means 8%
 * @param {'excluding'|'including'} vatType
 * @returns {{ netAmount: number, vatAmount: number, grossAmount: number }}
 */
export function calcVat(enteredPrice, vatPercent, vatType) {
    const price = enteredPrice || 0;
    const rate = (vatPercent || 0) / 100;

    if (rate <= 0) {
        return { netAmount: price, vatAmount: 0, grossAmount: price };
    }

    if (vatType === 'including') {
        // Entered price is the Gross Amount
        const vatAmount = price * rate; // VA = GA × rate
        const netAmount = price - vatAmount; // ST = GA − VA
        return { netAmount, vatAmount, grossAmount: price };
    }

    // Excluding VAT (default): entered price is the Subtotal
    const vatAmount = price * rate; // VA = ST × rate
    const grossAmount = price + vatAmount; // GA = ST + VA
    return { netAmount: price, vatAmount, grossAmount };
}

/**
 * Aggregate totals across all invoice line items.
 * @param {Array<{price, priceOffer, quantity, vatPercent, vatType}>} items
 * @returns {{ subTotal: number, vatTotal: number, grandTotal: number }}
 */
export function calcInvoiceTotals(items = []) {
    let subTotal = 0;
    let vatTotal = 0;

    items.forEach((item) => {
        const effectivePrice =
            item.priceOffer > 0 && item.priceOffer < item.price ? item.priceOffer : item.price;

        const { netAmount, vatAmount } = calcVat(effectivePrice, item.vatPercent, item.vatType);

        subTotal += netAmount * (item.quantity || 0);
        vatTotal += vatAmount * (item.quantity || 0);
    });

    return { subTotal, vatTotal, grandTotal: subTotal + vatTotal };
}

/**
 * Format the VAT cell display string, e.g. "8.00 (8%)"
 * Returns "--" when vatPercent is 0 or vatAmount is 0.
 * @param {number} vatAmount
 * @param {number} vatPercent
 * @returns {string}
 */
export function formatVatCell(vatAmount, vatPercent) {
    if (!vatPercent || vatPercent <= 0) return '--';
    return `${vatAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })} (${vatPercent}%)`;
}
